<template>
  <div
    :class="{
      'has-error': !!errorMessage,
    }"
  >
    <!-- Label -->
    <label v-if="label" :for="name" class="block mb-2 font-medium">
      <i v-if="icon" class="pi mr-2" :class="icon" />
      {{ label }}
      <span v-if="required" class="text-red-500 ml-1">*</span>
    </label>

    <!-- Edit Mode -->
    <Textarea
      v-if="meta?.mode === 'edit'"
      :id="name"
      v-model="inputValue"
      :name="name"
      :placeholder="placeholder"
      :disabled="disabled"
      :readonly="readOnly"
      :rows="rows"
      :cols="cols"
      :autoResize="autoResize"
      :maxlength="maxLength"
      :class="[
        className,
        'w-full',
        {
          'p-invalid': !!errorMessage,
        },
      ]"
      v-bind="attrs"
      @blur="handleBlur"
      @input="handleInputChange"
    />

    <!-- View Mode -->
    <div v-else class="font-medium text-color whitespace-pre-wrap">
      {{ inputValue || "-" }}
    </div>

    <!-- Character Counter -->
    <div
      v-if="showCounter && meta?.mode === 'edit'"
      class="flex justify-content-between mt-1"
    >
      <small v-if="!hideErrorMessage && errorMessage" class="p-error">
        {{ errorMessage }}
      </small>
      <small class="text-color-secondary ml-auto">
        {{ characterCount }} / {{ maxLength }}
      </small>
    </div>

    <!-- Error Message (without counter) -->
    <small
      v-else-if="!hideErrorMessage && errorMessage"
      class="p-error block mt-1"
    >
      {{ errorMessage }}
    </small>
  </div>
</template>

<script>
import { useField } from "vee-validate";
import { watch, computed } from "vue";

export default {
  name: "BaseTextarea",

  inject: ["meta"],

  props: {
    modelValue: {
      type: [String, Number],
      default: "",
    },
    name: {
      type: String,
      required: true,
    },
    label: {
      type: String,
      default: "",
    },
    icon: {
      type: String,
      default: "",
    },
    placeholder: {
      type: String,
      default: "",
    },
    className: {
      type: String,
      default: "",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    required: {
      type: Boolean,
      default: false,
    },
    rows: {
      type: Number,
      default: 3,
    },
    cols: {
      type: Number,
      default: 30,
    },
    autoResize: {
      type: Boolean,
      default: true,
    },
    maxLength: {
      type: Number,
      default: 1000,
    },
    showCounter: {
      type: Boolean,
      default: false,
    },
    hideErrorMessage: {
      type: Boolean,
      default: false,
    },
  },

  emits: ["update:modelValue", "blur", "input"],

  setup(props, { emit, attrs }) {
    // Setup vee-validate field
    const {
      value: inputValue,
      errorMessage,
      handleBlur,
      handleChange,
      meta: fieldMeta,
    } = useField(props.name, undefined, {
      initialValue: props.modelValue,
    });

    // Initialize with modelValue
    inputValue.value = props.modelValue;

    // Watch for external model changes
    watch(
      () => props.modelValue,
      (newValue) => {
        if (inputValue.value !== newValue) {
          inputValue.value = newValue;
        }
      },
    );

    // Character count
    const characterCount = computed(() => {
      return inputValue.value ? String(inputValue.value).length : 0;
    });

    // Handle input changes
    const handleInputChange = (event) => {
      const value = event.target.value;
      inputValue.value = value;
      handleChange(value);
      emit("update:modelValue", value);
      emit("input", value);
    };

    // Handle blur events
    const handleBlurEvent = (event) => {
      handleBlur(event);
      emit("blur", event);
    };

    return {
      inputValue,
      errorMessage,
      handleBlur: handleBlurEvent,
      handleInputChange,
      fieldMeta,
      characterCount,
      attrs,
    };
  },
};
</script>

<style scoped>
.field {
  margin-bottom: 1.5rem;
}

.has-error :deep(.p-inputtextarea),
.field.has-error :deep(.p-inputtextarea) {
  border-color: #e24c4c;
}

.has-error :deep(.p-inputtextarea:focus),
.field.has-error :deep(.p-inputtextarea:focus) {
  border-color: #e24c4c;
  box-shadow: 0 0 0 0.2rem rgba(226, 76, 76, 0.25);
}

.has-error :deep(small.p-error),
.field.has-error :deep(small.p-error) {
  color: #e24c4c !important;
}

.whitespace-pre-wrap {
  white-space: pre-wrap;
}
</style>
